import type { Schemas } from '@argzombie/api-client';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { unwrap, useApi } from '../lib/api';
import {
  PAGE_SIZE,
  purchaseCharacter,
  storeQuery as query,
  type PurchaseRow,
  type StoreFilters,
} from '../lib/playerSupport';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '-');

/** Production in plain text; any test environment highlighted, so a sandbox purchase is never mistaken for revenue. */
export function EnvironmentBadge({ environment }: { environment: string }) {
  return environment === 'production' ? (
    <span>{environment}</span>
  ) : (
    <span className="rounded bg-caption px-1 font-semibold">{environment}</span>
  );
}

function Pager({
  offset,
  take,
  total,
  onOffset,
}: {
  offset: number;
  take: number;
  total: number;
  onOffset: (offset: number) => void;
}) {
  if (total <= take && offset === 0) return null;
  const last = Math.min(offset + take, total);
  return (
    <div className="mt-2 flex items-center gap-2 text-sm">
      <button
        type="button"
        disabled={offset === 0}
        onClick={() => onOffset(Math.max(0, offset - take))}
        className="rounded border-2 border-ink px-2 disabled:opacity-40"
      >
        Previous
      </button>
      <span className="font-mono">
        {total === 0 ? 0 : offset + 1}–{last} of {total}
      </span>
      <button
        type="button"
        disabled={last >= total}
        onClick={() => onOffset(offset + take)}
        className="rounded border-2 border-ink px-2 disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

function PlayerCell({
  row,
  show,
}: {
  row: { playerId: string; playerName: string; email?: string | null };
  show: boolean;
}) {
  if (!show) return null;
  return (
    <td className={td}>
      <Link to="/players/$playerId" params={{ playerId: row.playerId }} className="underline">
        {row.playerName}
      </Link>
      {row.email && <div className="font-mono text-xs text-bruise">{row.email}</div>}
    </td>
  );
}

/** Purchases, newest first, one page at a time. `showPlayer` adds the player column (off on a player's own page). */
export function PurchasesTable({
  filters,
  offset,
  onOffset,
  showPlayer = true,
  take = PAGE_SIZE,
}: {
  filters: StoreFilters;
  offset: number;
  onOffset: (offset: number) => void;
  showPlayer?: boolean;
  take?: number;
}) {
  const { client } = useApi();
  const page = useQuery({
    queryKey: ['admin-purchases', filters, offset, take],
    queryFn: async () =>
      unwrap(
        await client.GET('/admin/purchases', { params: { query: query(filters, offset, take) } }),
      ),
  });

  if (page.isPending) return <p className="text-sm">Loading&hellip;</p>;
  if (page.isError) return <p className="text-sm text-blam">{String(page.error)}</p>;
  const rows: PurchaseRow[] = page.data.items;
  if (rows.length === 0) return <p className="text-sm text-bruise">No purchases.</p>;

  return (
    <>
      <table className="w-full border-2 border-ink bg-white text-sm">
        <thead className="bg-ink text-newsprint">
          <tr>
            <th className={th}>When</th>
            {showPlayer && <th className={th}>Player</th>}
            <th className={th}>Product</th>
            <th className={th}>Character</th>
            <th className={th}>Platform</th>
            <th className={th}>Environment</th>
            <th className={th}>Status</th>
            <th className={th}>Energy</th>
            <th className={th}>Transaction</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-newsprint align-top">
              <td className={`${td} font-mono whitespace-nowrap`}>{when(p.purchasedAt)}</td>
              <PlayerCell row={p} show={showPlayer} />
              <td className={td}>
                {p.productId}
                <div className="font-mono text-xs text-bruise">{p.storeProductId}</div>
                {p.productKind === 'resurrection' && (
                  <div className="text-xs">
                    resurrection{p.tier != null ? `, tier ${p.tier}` : ''}
                  </div>
                )}
              </td>
              <td className={td}>{purchaseCharacter(p)}</td>
              <td className={td}>{p.platform}</td>
              <td className={td}>
                <EnvironmentBadge environment={p.environment} />
              </td>
              <td
                className={`${td} ${p.status === 'refunded' || p.status === 'revoked' ? 'text-blam' : ''}`}
              >
                {p.status}
                {p.expiresAt ? <div className="text-xs">until {when(p.expiresAt)}</div> : null}
              </td>
              <td className={`${td} font-mono`}>{p.energy}</td>
              <td className={`${td} font-mono text-xs break-all`}>
                {p.transactionId}
                {p.originalTransactionId && p.originalTransactionId !== p.transactionId && (
                  <div className="text-bruise">orig {p.originalTransactionId}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager offset={offset} take={take} total={page.data.total} onOffset={onOffset} />
    </>
  );
}

/** Supporter pass subscriptions, most recently updated first. */
export function SubscriptionsTable({
  filters,
  offset,
  onOffset,
  showPlayer = true,
  take = PAGE_SIZE,
}: {
  filters: StoreFilters;
  offset: number;
  onOffset: (offset: number) => void;
  showPlayer?: boolean;
  take?: number;
}) {
  const { client } = useApi();
  const page = useQuery({
    queryKey: ['admin-subscriptions', filters, offset, take],
    queryFn: async () =>
      unwrap(
        await client.GET('/admin/subscriptions', {
          params: { query: query(filters, offset, take) },
        }),
      ),
  });

  if (page.isPending) return <p className="text-sm">Loading&hellip;</p>;
  if (page.isError) return <p className="text-sm text-blam">{String(page.error)}</p>;
  const rows: Schemas['AdminSubscriptionRow'][] = page.data.items;
  if (rows.length === 0) return <p className="text-sm text-bruise">No subscriptions.</p>;

  return (
    <>
      <table className="w-full border-2 border-ink bg-white text-sm">
        <thead className="bg-ink text-newsprint">
          <tr>
            <th className={th}>Updated</th>
            {showPlayer && <th className={th}>Player</th>}
            <th className={th}>Product</th>
            <th className={th}>Platform</th>
            <th className={th}>Environment</th>
            <th className={th}>Status</th>
            <th className={th}>Expires</th>
            <th className={th}>Renews</th>
            <th className={th}>Original transaction</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="border-t border-newsprint align-top">
              <td className={`${td} font-mono whitespace-nowrap`}>{when(s.updatedAt)}</td>
              <PlayerCell row={s} show={showPlayer} />
              <td className={td}>{s.productId}</td>
              <td className={td}>{s.platform}</td>
              <td className={td}>
                <EnvironmentBadge environment={s.environment} />
              </td>
              <td className={`${td} ${s.status === 'revoked' ? 'text-blam' : ''}`}>{s.status}</td>
              <td className={`${td} font-mono whitespace-nowrap`}>{when(s.expiresAt)}</td>
              <td className={td}>{s.autoRenew ? 'yes' : 'no'}</td>
              <td className={`${td} font-mono text-xs break-all`}>{s.originalTransactionId}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager offset={offset} take={take} total={page.data.total} onOffset={onOffset} />
    </>
  );
}
